import React, { useEffect, useMemo, useState } from 'react';

import {
  AddIcon,
  Button,
  DateRangePicker,
  Divider,
  Form,
  Input,
  Pagination,
  Radio,
  RadioGroup,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  TypeSelectOption,
  toastService,
} from '@thefarmersfront/kitchen';

import dayjs from 'dayjs';

import { PageTitle, Table, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import { VenderSupplierAddModal, VenderSupplierDetailModal } from '~/components/vender';

import { useModal, useStableCallback, useToggle } from '~/hooks/common';
import { useGetOwnerSupplier, useGetOwnerSupplierListCodes } from '~/hooks/vender';
import { useLoadingStore } from '~/store';
import { getVenderSupplierColumnDefs, renderFilterErrorMessage, transformToSelectOptions } from '~/utils';

import {
  BREAD_CRUMB,
  INITIAL_VENDER_PAGE_CURSOR,
  PATH,
  TOAST_MESSAGE,
  VENDER_DATE_FORMAT,
  VENDER_PAGE_SIZE,
} from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeVenderSupplier } from '~/types/vender.types';

import styles from './VenderSupplierPage.module.scss';

const VenderSupplierPage = () => {
  const PAGE_LIMIT_OPTIONS = Object.values(VENDER_PAGE_SIZE);

  const { getOwnerSupplierListCodes } = useGetOwnerSupplierListCodes();
  const { getOwnerSupplier } = useGetOwnerSupplier();

  const { setIsLoading } = useLoadingStore();

  const [venderSupplierFilterOptions, setVenderSupplierFilterOptions] = useState<{
    searchFieldOption: TypeSelectOption<string>[];
    dateOption: TypeSelectOption<string>[];
  }>({ searchFieldOption: [], dateOption: [] });

  const [supplierSelectValue, setSupplierSelectValue] = useState<TypeSelectOption<string>>();
  const [supplierInputValue, setSupplierInputValue] = useState('');
  const [dateRangeSelectValue, setDateRangeSelectValue] = useState<TypeSelectOption<string>>();
  const [dateRangeCalendarValue, setDateRangeCalendarValue] = useState<{
    start: Date | null;
    end: Date | null;
  } | null>(null);
  const [operationRadioValue, setOperationRadioValue] = useState<'Y' | 'N'>('Y');

  const [supplierInfo, setSupplierInfo] = useState<TypeVenderSupplier[] | null>(null);
  const [selectedOwnerSupplierCode, setSelectedOwnerSupplierCode] = useState('');

  const [itemTotalCount, setItemTotalCount] = useState(0);
  const [pageLimitSelectValue, setPageLimitSelectValue] = useState<TypeSelectOption<number>>(PAGE_LIMIT_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(INITIAL_VENDER_PAGE_CURSOR);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const {
    isOpen: isOpenSupplierAddModal,
    handleOpenModal: handleOpenSupplierAddModal,
    handleCloseModal: handleCloseSupplierAddModal,
    handleConfirmModal: handleConfirmSupplierAddModal,
  } = useModal({
    onConfirm: () => handleSearch(),
  });

  const {
    isOpen: isOpenSupplierDetailModal,
    handleOpenModal: handleOpenSupplierDetailModal,
    handleCloseModal: handleCloseSupplierDetailModal,
    handleConfirmModal: handleConfirmSupplierDetailModal,
  } = useModal({
    onOpen: (data?: TypeVenderSupplier) => {
      if (!data) return;
      setSelectedOwnerSupplierCode(data.ownerSupplierCode ?? '');
    },
    onConfirm: () => handleSearch(),
  });

  const handleClickViewDetail = useStableCallback(handleOpenSupplierDetailModal);
  const { columnDefs } = useMemo(
    () => getVenderSupplierColumnDefs({ onClickViewDetail: handleClickViewDetail }),
    [handleClickViewDetail]
  );

  const fetchOwnerSupplierListCodes = useStableCallback(async () => {
    try {
      setIsLoading(true);

      const { searchFieldOption, dateOption } = await getOwnerSupplierListCodes();

      const formattedFilterOptions = {
        searchFieldOption: transformToSelectOptions(searchFieldOption, 'code', 'description'),
        dateOption: transformToSelectOptions(dateOption, 'code', 'description'),
      };

      setVenderSupplierFilterOptions(formattedFilterOptions);
      setSupplierSelectValue(formattedFilterOptions.searchFieldOption[0]);
      setDateRangeSelectValue(formattedFilterOptions.dateOption[0]);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        persist: true,
        message: (error as TypeErrorResponseData['error'])?.message || renderFilterErrorMessage(),
      });
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    fetchOwnerSupplierListCodes();
  }, [fetchOwnerSupplierListCodes]);

  const fetchVenderSupplierInfo = async (page: number, pageLimit?: number) => {
    const formattedDateFrom = dateRangeCalendarValue?.start
      ? dayjs(dateRangeCalendarValue.start).format(VENDER_DATE_FORMAT.DATE_TIME)
      : null;
    const formattedDateTo = dateRangeCalendarValue?.end
      ? dayjs(dateRangeCalendarValue.end).format(VENDER_DATE_FORMAT.DATE_TIME)
      : null;

    try {
      setIsLoading(true);

      const params = {
        searchField: supplierSelectValue?.value ?? '',
        searchWord: supplierInputValue,
        dateOptionType: dateRangeSelectValue?.value ?? '',
        dateFrom: formattedDateFrom,
        dateTo: formattedDateTo,
        useStatus: operationRadioValue,
        cursor: page,
        limit: pageLimit ?? pageLimitSelectValue.value,
      };

      const {
        pagination: { listTotal },
        data,
      } = await getOwnerSupplier(params);

      setItemTotalCount(listTotal);
      setSupplierInfo(data);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterResetClick = () => {
    setSupplierSelectValue(venderSupplierFilterOptions.searchFieldOption[0]);
    setSupplierInputValue('');
    setDateRangeSelectValue(venderSupplierFilterOptions.dateOption[0]);
    setDateRangeCalendarValue(null);
    setOperationRadioValue('Y');
  };

  const handleSupplierSelectChange = (_: unknown, selectedOption: TypeSelectOption) => {
    setSupplierSelectValue(selectedOption as TypeSelectOption<string>);
    setSupplierInputValue('');
  };

  const handleSupplierInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierInputValue(e.target.value);
  };

  const handleDateRangeSelectChange = (_: unknown, selectedOption: TypeSelectOption) => {
    setDateRangeSelectValue(selectedOption as TypeSelectOption<string>);
    setDateRangeCalendarValue(null);
  };

  const handleDateRangeInputChange = (
    value: {
      start: Date | null;
      end: Date | null;
    } | null
  ) => {
    setDateRangeCalendarValue(value);
  };

  const handleOperationRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOperationRadioValue(e.target.value as 'Y' | 'N');
  };

  const handleTableLimitChange = async (_: unknown, selectedOption: TypeSelectOption) => {
    setPageLimitSelectValue(selectedOption as TypeSelectOption<number>);

    await fetchVenderSupplierInfo(1, selectedOption.value as number);
  };

  const handleCurrentPageChange = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const selectedPage = Number(e.currentTarget.value);

    setCurrentPage(selectedPage);

    await fetchVenderSupplierInfo(selectedPage);
  };

  const handleSearch = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    setCurrentPage(INITIAL_VENDER_PAGE_CURSOR);

    await fetchVenderSupplierInfo(INITIAL_VENDER_PAGE_CURSOR);
  };

  return (
    <PageWrapper>
      <PageTitle
        title="공급사 관리"
        breadcrumbs={BREAD_CRUMB[PATH.VENDER_SUPPLIER]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <Form className={styles['filter-section']} onSubmit={handleSearch}>
          <div className={styles['filters-container']}>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                공급사
              </Text>
              <div className={styles['input-container']}>
                <Select
                  className={styles['input-select']}
                  size="small"
                  options={venderSupplierFilterOptions.searchFieldOption}
                  value={supplierSelectValue}
                  onChange={handleSupplierSelectChange}
                />
                <Input
                  className={styles['supplier-input']}
                  type="text"
                  size="small"
                  value={supplierInputValue}
                  placeholder="입력"
                  maxLength={200}
                  onChange={handleSupplierInputChange}
                />
              </div>
            </div>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                기간
              </Text>
              <div className={styles['input-container']}>
                <Select
                  className={styles['input-select']}
                  size="small"
                  options={venderSupplierFilterOptions.dateOption}
                  value={dateRangeSelectValue}
                  onChange={handleDateRangeSelectChange}
                />
                <DateRangePicker
                  className={styles['date-range-input']}
                  size="small"
                  allowSingleDateSelect
                  placeholder={{ start: '날짜 선택', end: '날짜 선택' }}
                  value={dateRangeCalendarValue}
                  onChange={handleDateRangeInputChange}
                />
              </div>
            </div>
            <div className={styles['operation-filter-container']}>
              <Text className={styles['filter-label']} size="small" weight="semi-bold">
                사용여부
              </Text>
              <RadioGroup
                className={styles['operation-radio-group']}
                name="operation-radio-group"
                size="medium"
                value={operationRadioValue}
                onChange={handleOperationRadioChange}
              >
                <Radio label="사용" value={'Y'} />
                <Radio label="사용안함" value={'N'} />
              </RadioGroup>
            </div>
          </div>
          <div className={styles['filter-actions-container']}>
            <Button className={styles['filter-search-button']} size="small" type="submit">
              검색
            </Button>
            <TextButton
              className={styles['filter-reset-button']}
              leftIcon={<RefreshIcon size={16} />}
              size="small"
              color="secondary"
              onClick={handleFilterResetClick}
            >
              필터 초기화
            </TextButton>
          </div>
          <Divider color="gray100" />
        </Form>
      )}
      <div className={styles['table-section']}>
        <TableTopField
          dataTotalCount={itemTotalCount}
          rightButton={
            <Button size="tiny" color="secondary" leftIcon={<AddIcon size={16} />} onClick={handleOpenSupplierAddModal}>
              공급사 등록
            </Button>
          }
        />
        <div className={styles['table-wrapper']}>
          <Table data={supplierInfo} columns={columnDefs} rowStatusType={supplierInfo === null ? 'INIT' : 'EMPTY'} />
        </div>
      </div>
      <div className={styles['table-actions-section']}>
        <Select
          className={styles['table-limit-select']}
          options={PAGE_LIMIT_OPTIONS}
          value={pageLimitSelectValue}
          size="tiny"
          dropdownPlacement="top-left"
          onChange={handleTableLimitChange}
        />
        {itemTotalCount > 0 ? (
          <Pagination
            className={styles['table-pagination']}
            currentPage={currentPage}
            totalCount={itemTotalCount}
            pageSize={pageLimitSelectValue.value}
            onClick={handleCurrentPageChange}
          />
        ) : null}
      </div>
      {isOpenSupplierAddModal && (
        <VenderSupplierAddModal
          open={isOpenSupplierAddModal}
          onCancel={handleCloseSupplierAddModal}
          onConfirm={handleConfirmSupplierAddModal}
        />
      )}
      {isOpenSupplierDetailModal && (
        <VenderSupplierDetailModal
          open={isOpenSupplierDetailModal}
          ownerSupplierCode={selectedOwnerSupplierCode}
          onCancel={handleCloseSupplierDetailModal}
          onConfirm={handleConfirmSupplierDetailModal}
        />
      )}
    </PageWrapper>
  );
};

export default VenderSupplierPage;
